import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Button, Stack, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, FormControl, InputLabel, Select, MenuItem, Divider,
} from "@mui/material";
import { Add, Edit, Delete, People } from "@mui/icons-material";
import {
  listGroups, createGroup, updateGroup, deleteGroup,
  listGroupMembers, addGroupMember, removeGroupMember, listUsers,
  GroupRow, UserRow,
} from "../../api";

export default function GroupsAdmin() {
  const [rows, setRows] = useState<GroupRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const [draft, setDraft] = useState<{ _new: boolean; id?: number; name: string; description: string } | null>(null);

  const [membersOf, setMembersOf] = useState<GroupRow | null>(null);
  const [members, setMembers] = useState<{ user_id: number; username: string; role: string }[]>([]);
  const [addUid, setAddUid] = useState<number | "">("");
  const [addRole, setAddRole] = useState("member");

  const load = useCallback(async () => {
    setErr(null);
    try { setRows(await listGroups()); } catch (e: any) { setErr(e.message); }
    try { setUsers(await listUsers()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    setErr(null);
    try {
      if (draft._new) await createGroup({ name: draft.name.trim(), description: draft.description || undefined });
      else await updateGroup(draft.id!, { name: draft.name.trim(), description: draft.description || undefined });
      setDraft(null); await load();
    } catch (e: any) { setErr(e.message); }
  };
  const remove = async (g: GroupRow) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    setErr(null);
    try { await deleteGroup(g.id); await load(); } catch (e: any) { setErr(e.message); }
  };

  const openMembers = async (g: GroupRow) => {
    setMembersOf(g); setAddUid(""); setAddRole("member");
    try { setMembers(await listGroupMembers(g.id)); } catch (e: any) { setErr(e.message); }
  };
  const reloadMembers = async () => { if (membersOf) setMembers(await listGroupMembers(membersOf.id)); };
  const doAddMember = async () => {
    if (!membersOf || addUid === "") return;
    setErr(null);
    try { await addGroupMember(membersOf.id, Number(addUid), addRole); setAddUid(""); await reloadMembers(); }
    catch (e: any) { setErr(e.message); }
  };
  const doRemoveMember = async (uid: number) => {
    if (!membersOf) return;
    try { await removeGroupMember(membersOf.id, uid); await reloadMembers(); } catch (e: any) { setErr(e.message); }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Groups</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDraft({ _new: true, name: "", description: "" })}>New group</Button>
        </Stack>
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Name</TableCell><TableCell>Description</TableCell><TableCell align="right">Actions</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {rows.map((g) => (
              <TableRow key={g.id} hover>
                <TableCell>{g.name}</TableCell>
                <TableCell>{g.description || ""}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" title="Members" onClick={() => openMembers(g)}><People fontSize="small" /></IconButton>
                  <IconButton size="small" title="Edit" onClick={() => setDraft({ _new: false, id: g.id, name: g.name, description: g.description || "" })}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" title="Delete" onClick={() => remove(g)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}>No groups.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>

      {/* Create/edit */}
      <Dialog open={!!draft} onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft?._new ? "New group" : `Edit ${draft?.name}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth value={draft?.name || ""} onChange={(e) => draft && setDraft({ ...draft, name: e.target.value })} autoFocus />
            <TextField label="Description" fullWidth value={draft?.description || ""} onChange={(e) => draft && setDraft({ ...draft, description: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!draft?.name?.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Members */}
      <Dialog open={!!membersOf} onClose={() => setMembersOf(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Members of {membersOf?.name}</DialogTitle>
        <DialogContent>
          <Table size="small" sx={{ mb: 2 }}>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.user_id}>
                  <TableCell>{m.username}</TableCell>
                  <TableCell><Chip size="small" label={m.role} color={m.role === "admin" ? "secondary" : "default"} /></TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => doRemoveMember(m.user_id)} title="Remove"><Delete fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {members.length === 0 && <TableRow><TableCell colSpan={3} align="center">No members.</TableCell></TableRow>}
            </TableBody>
          </Table>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" gap={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Add user</InputLabel>
              <Select label="Add user" value={addUid} onChange={(e) => setAddUid(e.target.value as number)}>
                {users.filter((u) => !members.some((m) => m.user_id === u.id)).map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={addRole} onChange={(e) => setAddRole(e.target.value)}>
                <MenuItem value="member">member</MenuItem>
                <MenuItem value="admin">admin</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={doAddMember} disabled={addUid === ""}>Add</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMembersOf(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
